import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IIDolApparatoStore } from './iidOlApparato.store';
import { IID_OL_APPARATO } from '@common/interfaces/interfaceData';
import { ActionService } from '../action/action.service';
import { Topics } from '@common/interfaces/topics';
import { IIDolApparatoQuery } from './iidOlApparato.query'
import { CrudService } from '../crudService';
import { ProjectEntityStoreStrategy } from '../updateStoreStrategies';
import { ProvisioningSessionExpiration } from '@common/interfaces/zeusProvisioning';
import { InterfaceDataService } from '../interfaceDataRegistry';
import { InterfaceName } from '@common/interfaces/issueTypeInterface';
import { MaterialCodeSourceItem, MobileCompatibilityResponse } from '@common/dto/magazzino.types';
import { MagazzinoPaginationDTO, MaterialCodeSourceFilters, PaginatedResponse } from '@common/dto/magazzino.dto';
import { GatewayResult, WarehouseHttpService } from '@ep-om/project/pages/warehouse/gateway/WarehouseHttp.service';

export type ApparatoMagazzinoTransactionStatus = 'OPEN' | 'APPROVED' | 'REJECTED' | 'CREATE_FAILED';

export interface ApparatoMagazzinoError {
  code: string;
  message: string;
  details?: unknown;
}

/** FE mirror of the backend `ApparatoMovementTransaction` row (`GET .../apparato-magazzino`). */
export interface ApparatoMagazzinoTransaction {
  id: string;
  issueId: string;
  projectId: string;
  warehouseId: string | null;
  gisfoTransactionId: string | null;
  serials: { serial: string; warehouseItemId: string; tipologiaApparato: string }[];
  status: ApparatoMagazzinoTransactionStatus;
  error: ApparatoMagazzinoError | null;
  resolutionReason: string | null;
  createdBy: string;
  createdAt: string;
  resolvedAt: string | null;
}

/** FE mirror of the backend `ReconcileResult` (`POST .../apparato-magazzino/reconcile`). */
export interface ApparatoMagazzinoReconcileResult {
  status: 'NOOP' | 'CREATED' | 'RECREATED' | 'FAILED';
  transaction?: ApparatoMagazzinoTransaction;
  error?: ApparatoMagazzinoError;
}

export interface ApparatoCompatibilitySearchBody {
  serialCode?: string;
  alias: string;
  sortBy?: unknown[];
  pagination?: Record<string, unknown>;
}

/** `warehouse` envelope of the provisioning-request response — pinned contract, single source in @common. */
export type { ProvisioningWarehouseEnvelope } from '@common/interfaces/zeusProvisioning';
@Injectable({
  providedIn: 'root'
})
@InterfaceDataService(InterfaceName.APPARATO)
export class IIDolApparatoService extends CrudService<IID_OL_APPARATO, IIDolApparatoStore, IIDolApparatoQuery> {
  constructor(
    protected store: IIDolApparatoStore,
    public query: IIDolApparatoQuery,
    protected actionService: ActionService,
    protected updateStrategy: ProjectEntityStoreStrategy,
    protected httpClient: HttpClient,
    private warehouseHttp: WarehouseHttpService) {
    super(
      Topics.IID_OL_APPARATO,
      store,
      query,
      actionService,
      updateStrategy
    );
  }

  requestProvisioning(issueId: string, typeInterfaceName: string): Observable<unknown> {
    return this.httpClient.post(
      `${environment.apiUrl}/api/v1/orders/${issueId}/provisioning-request`,
      { typeInterfaceName },
    );
  }

  getLatestProvisioningExpiration(issueId: string): Observable<ProvisioningSessionExpiration | null> {
    return this.httpClient.get<ProvisioningSessionExpiration | null>(
      `${environment.apiUrl}/api/v1/orders/${issueId}/provisioning-session/latest-expiration`,
    );
  }

  requestReplace(issueId: string, oldSerial: string, newSerial: string, typeInterfaceName: string): Observable<unknown> {
    return this.httpClient.post(`${environment.apiUrl}/api/v1/orders/${issueId}/replace-request`, {
      oldSerial,
      newSerial,
      typeInterfaceName,
    });
  }

  searchCompatibility(issueId: string, body: ApparatoCompatibilitySearchBody): Observable<MobileCompatibilityResponse> {
    return this.httpClient.post<MobileCompatibilityResponse>(
      `${environment.apiUrl}/api/v1/orders/${issueId}/apparato-compatibility`,
      body,
    );
  }

  /**
   * `tipologiaApparato` picklist: material aliases enabled for the operator on the network. Not
   * issue-scoped — it also runs while the task is being created, so the caller resolves
   * `provider`/`sourceValue` from its draft request rows. Silent: the picker degrades to no options.
   */
  searchMaterialCodesSources(
    filters: MaterialCodeSourceFilters,
    pagination: MagazzinoPaginationDTO,
  ): Observable<GatewayResult<PaginatedResponse<MaterialCodeSourceItem>>> {
    return this.warehouseHttp.dispatch({
      operation: 'materialCodesSources.list',
      listOptions: { filters, pagination },
    }, { silent: true });
  }

  getMagazzinoTransaction(issueId: string): Observable<ApparatoMagazzinoTransaction | null> {
    return this.httpClient.get<ApparatoMagazzinoTransaction | null>(
      `${environment.apiUrl}/api/v1/orders/${issueId}/apparato-magazzino`,
    );
  }

  reconcileMagazzino(issueId: string): Observable<ApparatoMagazzinoReconcileResult> {
    return this.httpClient.post<ApparatoMagazzinoReconcileResult>(
      `${environment.apiUrl}/api/v1/orders/${issueId}/apparato-magazzino/reconcile`,
      {},
    );
  }

  rejectMagazzino(issueId: string): Observable<void> {
    return this.httpClient.post<void>(
      `${environment.apiUrl}/api/v1/orders/${issueId}/apparato-magazzino/reject`,
      { reason: 'modify' },
    );
  }

}
